import { defineStore } from "pinia";
import { useRuntimeConfig } from "nuxt/app";
import { useCustomAsyncStoryblok } from "../composables/CustomAsyncStoryblok";

export const useDatasourcesStore = defineStore("datasources", {
  state: () => ({
    entries: null,
    tagEntries: null,
    settings: null,
    countrySettings: [],
  }),

  getters: {
    getEntries: (state): any => {
      return state.entries;
    },
    getTagEntries: (state): any => {
      return state.tagEntries;
    },
    getSettings: (state): any => {
      return state.settings;
    },
    getWhatsappUrl: (state): string | null => {
      const entry = state.countrySettings?.find((e) => e.name === "chat.whatsapp.url");
      return entry?.value || null;
    },
    getLimitInputField: (state): string | null => {
      const entry = state.countrySettings?.find((e) => e.name === "form-input-characters-fields-limit");
      return entry?.value || null;
    },
    getStickyContactActivated: (state): boolean => {
      const entry = state.countrySettings?.find((e) => e.name === "sticky.contact.is.activated");
      return entry?.value === "true" || entry?.value === true;
    },
    getStickyContactExcludeTemplates: (state): string[] => {
      const entry = state.countrySettings?.find((e) => e.name === "sticky.contact.exclude.template");
      if (!entry?.value) return [];
      return entry.value.split(',').map((t: string) => t.trim());
    },
    getFacilityPageContentTypes: (state): string[] => {
      const entry = state.countrySettings?.find((e) => e.name === "facility-page-content-type");
      if (!entry?.value) return [];
      return entry.value.split(',').map((t: string) => t.trim());
    },
    getCountrySettings: (state) => {
      return state.countrySettings;
    },
    getGmapCookiesActivated: (state): boolean => {
      const entry = state.countrySettings?.find((e) => e.name === "gmap.cookies.is.activated");
      return entry?.value === "true" || entry?.value === true;
    },
    getAbTestingConvertActivated: (state): boolean => {
      const entry = state.countrySettings?.find((e) => e.name === "ab.testing.convert.is.activated");
      return entry?.value === "true" || entry?.value === true;
    },
    getVwoAbTestingActivated: (state): boolean => {
      const entry = state.countrySettings?.find((e) => e.name === "vwo.ab-testing.is.activated");
      return entry?.value === "true" || entry?.value === true;
    },
    getStrictEmailValidationActivated: (state): boolean => {
      const entry = state.countrySettings?.find((e) => e.name === "form.email.strict.validation.is.activated");
      return entry?.value === "true" || entry?.value === true;
    },
  },

  actions: {
    async fetchDatasource(
      datasourceName: string,
      stateKey: string,
      additionalParams: Record<string, any> = {},
      useCache = true
    ): Promise<any> {
      if (useCache && this[stateKey] && (Array.isArray(this[stateKey]) ? this[stateKey].length > 0 : this[stateKey] !== null)) {
        return this[stateKey];
      }

      const config = useRuntimeConfig();

      try {
        const response = await useCustomAsyncStoryblok("cdn/datasource_entries", {
          datasource: datasourceName,
          per_page: 1000,
          ...additionalParams,
        });

        if (stateKey === 'countrySettings') {
          this[stateKey] = response.value?.datasource_entries || [];
        } else {
          this[stateKey] = response.value;
        }

        return this[stateKey];
      } catch (error) {
        console.error(`Error fetching datasource ${datasourceName}:`, error);
        if (stateKey === 'countrySettings') {
          return [];
        }
        return null;
      }
    },

    async fetchEntries(useCache = true) {
      const config = useRuntimeConfig();
      return this.fetchDatasource(
        config.public["country"],
        "entries",
        {},
        useCache
      );
    },

    async fetchTagEntries(useCache = true) {
      const config = useRuntimeConfig();
      return this.fetchDatasource(
        "all-countries-tags",
        "tagEntries",
        { dimension: config.public["country"] },
        useCache
      );
    },

    async fetchSettings(useCache = true) {
      const config = useRuntimeConfig();
      return this.fetchDatasource(
        `${config.public["storyblokCountryPrefix"]}-mapping-folders`,
        "settings",
        {},
        useCache
      );
    },

    async fetchCountrySettings(useCache = true) {
      const config = useRuntimeConfig();
      return this.fetchDatasource(
        `${config.public["storyblokCountryPrefix"]}-country-settings`,
        "countrySettings",
        {},
        useCache
      );
    },

    async fetchAll() {
      await Promise.all([
        this.fetchEntries(),
        this.fetchTagEntries(),
        this.fetchSettings(),
        this.fetchCountrySettings()
      ]);
    },

    findCountrySetting(key: string): string | null {
      const entry = this.countrySettings?.find((e) => e.name === key);
      return entry?.value || null;
    },
  },
});
